import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { GRUPY_NARZEDZI } from '../../config/nawigacja'
import { KlodkaLogowania, useWymagaLogowania } from '../auth/BramkaLogowania'
import { KlodkaPremium, useWymagaPremium } from '../plany/BramkaPremium'

/**
 * Czy pozycja paska odpowiada bieżącemu adresowi.
 *
 * Porównanie po CAŁYCH segmentach: `/panele` nie może świecić na `/panel/12`
 * przez samo podobieństwo napisu, tylko dlatego, że `/panel` stoi w `takze`.
 */
export const czyAktywna = (pozycja, sciezka) =>
  [pozycja.to, ...(pozycja.takze || [])].some(
    (adres) => sciezka === adres || sciezka.startsWith(`${adres}/`),
  )

/**
 * Jedna pozycja: ikona w niebieskim kafelku i pełna nazwa. Wspólna dla paska,
 * szuflady i menu „Narzędzia” w górnym pasku, żeby wszędzie wyglądała tak samo.
 *
 * Więcej niebieskiego (prośba właściciela 2026-10-07): każda ikona stoi w jasnoniebieskim kafelku,
 * jak ikony w nagłówkach stron. Aktywną pozycję wyróżnia kafelek wypełniony na niebiesko i tło
 * wiersza, więc od razu widać, gdzie się jest.
 *
 * `kreska`: pionowa kreska przy lewej krawędzi paska lub szuflady (`-left-3` = ich wewnętrzny
 * margines). W menu z kolumnami jej nie ma, bo wypadałaby w odstępie między kolumnami.
 */
export const PozycjaNarzedzia = ({ pozycja, aktywna, zwiniety = false, kreska = true, onWybor }) => {
  const { to, label, Icon } = pozycja
  // Narzędzie za logowaniem (`bramka` w config/nawigacja): niezalogowany widzi kłódkę przy nazwie.
  const zamkniete = useWymagaLogowania(pozycja.bramka)
  // Funkcja planu Premium (`funkcja`, FE-04): zalogowany bez niej widzi plakietkę „Premium”.
  const premium = useWymagaPremium(pozycja.funkcja)
  const dopisek = (zamkniete && ' (po zalogowaniu)') || (premium && ' (w planie Premium)') || ''
  return (
    <Link
      to={to}
      onClick={onWybor}
      aria-current={aktywna ? 'page' : undefined}
      title={zwiniety ? `${label}${dopisek}` : undefined}
      className={`group relative flex items-center rounded-xl text-sm transition-colors
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500
                  ${zwiniety ? 'justify-center h-9 w-10 mx-auto' : 'gap-2.5 px-2 py-1'}
                  ${aktywna
                    ? 'bg-blue-50 text-blue-700 font-semibold'
                    : 'text-gray-700 font-medium hover:text-blue-700 hover:bg-blue-50/70'}`}
    >
      {aktywna && kreska && (
        <span
          aria-hidden='true'
          className='absolute -left-3 top-1 bottom-1 w-1 rounded-r-full bg-blue-600'
        />
      )}
      <span
        aria-hidden='true'
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg transition-colors ${aktywna
          ? 'bg-blue-600 text-white shadow-sm shadow-blue-600/30'
          : 'bg-blue-50 text-blue-600 group-hover:bg-blue-100'}`}
      >
        <Icon className='w-[18px] h-[18px]' />
      </span>
      <span className={zwiniety ? 'sr-only' : 'whitespace-nowrap'}>{label}</span>
      {zamkniete && (zwiniety
        ? <span className='sr-only'> (po zalogowaniu)</span>
        : <KlodkaLogowania />)}
      {premium && (zwiniety
        ? <span className='sr-only'> (w planie Premium)</span>
        : <KlodkaPremium />)}
    </Link>
  )
}

/**
 * Lista narzędzi w grupach z `config/nawigacja`. Trzy układy:
 * - pionowy (szuflada na telefonie, pasek boczny rozwinięty),
 * - `zwiniety`: same ikony, nazwa w `title` (dymek przeglądarki) i w `sr-only` (czytnik ekranu);
 *   nagłówki grup zostają dla czytnika, a na ekranie zastępuje je cienka kreska,
 * - `kolumny`: grupy obok siebie, dla menu „Narzędzia” w górnym pasku. Kolumny mają szerokość
 *   treści (`max-content`), więc najdłuższa nazwa nigdy się nie łamie, a panel jest tak szeroki,
 *   jak trzeba, i nie wyjeżdża poza ekran na laptopie.
 */
const ListaNarzedzi = ({ zwiniety = false, kolumny = false, onWybor, idPrefix = 'pasek' }) => {
  const { pathname } = useLocation()

  const uklad = kolumny
    ? 'grid grid-cols-[repeat(4,max-content)] gap-x-5'
    : `flex flex-col ${zwiniety ? 'gap-2' : 'gap-3'}`

  return (
    <div className={uklad}>
      {GRUPY_NARZEDZI.map((grupa, gi) => {
        const idNaglowka = `${idPrefix}-grupa-${gi}`
        return (
          <div key={grupa.tytul}>
            <p
              id={idNaglowka}
              className={zwiniety
                ? 'sr-only'
                : `px-3 text-xs font-semibold text-blue-600/80 ${kolumny ? 'pb-2' : 'pb-1'}`}
            >
              {grupa.tytul}
            </p>
            {zwiniety && gi > 0 && (
              <div aria-hidden='true' className='mx-2 mb-2 border-t border-blue-100' />
            )}
            <ul aria-labelledby={idNaglowka} className='flex flex-col gap-0.5'>
              {grupa.pozycje.map((pozycja) => (
                <li key={pozycja.to}>
                  <PozycjaNarzedzia
                    pozycja={pozycja}
                    aktywna={czyAktywna(pozycja, pathname)}
                    zwiniety={zwiniety}
                    kreska={!kolumny}
                    onWybor={onWybor}
                  />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

export default ListaNarzedzi
